/**
 * Enable-alerts — FULL-SCREEN notification permission gate (user rule
 * 2026-07-20: the old dismissible home card was too subtle; without
 * notifications the whole approval loop collapses, so the ask must be a
 * proper takeover that explains WHY — studiio/TradePulse pattern).
 * Shown after login on native when permission isn't granted (once per cold
 * start; keeps returning until granted). If the OS says "can't ask again",
 * the CTA deep-links to Settings instead.
 */
import { router } from 'expo-router';
import React from 'react';
import { AppState, Linking, Platform, Pressable, ScrollView, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, Screen } from '@/ui';

export default function EnableAlertsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [canAsk, setCanAsk] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (Platform.OS === 'web') return;
    const check = async (initial: boolean) => {
      const { pushPermissionStatus } = await import('@/lib/push');
      const status = await pushPermissionStatus();
      if (status === 'granted') {
        // Already allowed (or just allowed in Settings) — this screen is done.
        if (initial) router.back();
        else if (router.canGoBack()) router.back();
        else router.replace('/(tabs)/home');
        return;
      }
      setCanAsk(status === 'undetermined');
    };
    void check(true);
    // Re-check whenever the app returns to foreground — the user may have
    // granted permission in Settings; without this the screen never notices.
    const sub = AppState.addEventListener('change', (s) => { if (s === 'active') void check(false); });
    return () => sub.remove();
  }, []);

  const leave = () => {
    if (router.canGoBack()) router.back();
    else router.replace('/(tabs)/home');
  };

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!canAsk) {
        await Linking.openSettings();
        return;
      }
      const { registerForPush, pushPermissionStatus } = await import('@/lib/push');
      const ok = await registerForPush();
      if (ok) { leave(); return; }
      // registerForPush can fail AFTER permission was granted (push-token fetch
      // or the backend POST) — that must not read as "denied". Trust the actual
      // permission: granted → move on (token re-registers silently next launch).
      const status = await pushPermissionStatus();
      if (status === 'granted') { leave(); return; }
      setCanAsk(status === 'undetermined'); // truly denied → next tap goes to Settings
    } catch {
      // never trap the user on this screen
    } finally {
      setBusy(false);
    }
  };

  const REASONS: { icon: IconName; title: string; body: string }[] = [
    { icon: 'user-plus', title: t('alerts_r1_title'), body: t('alerts_r1_body') },
    { icon: 'package', title: t('alerts_r2_title'), body: t('alerts_r2_body') },
    { icon: 'megaphone', title: t('alerts_r3_title'), body: t('alerts_r3_body') },
    { icon: 'alert-triangle', title: t('alerts_r4_title'), body: t('alerts_r4_body') },
  ];

  return (
    <Screen scroll={false} safeTop>
      <ScrollView contentContainerStyle={{ flexGrow: 1, paddingHorizontal: space.s5, paddingTop: space.s8 }} showsVerticalScrollIndicator={false}>
        <View style={{ alignItems: 'center' }}>
          {/* bell-on-arch hero — big, warm, unmissable */}
          <View style={{
            width: 96, height: 108, backgroundColor: colors.primary100,
            borderTopLeftRadius: 48, borderTopRightRadius: 48, borderBottomLeftRadius: 14, borderBottomRightRadius: 14,
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="bell" size={44} color={colors.primary700} />
          </View>
          <AppText variant="displayLg" display style={{ marginTop: space.s5, textAlign: 'center' }}>{t('alerts_title')}</AppText>
          <AppText variant="bodyLg" tone="secondary" style={{ marginTop: space.s3, textAlign: 'center' }}>
            {t('alerts_sub')}
          </AppText>
        </View>

        <View style={{ marginTop: space.s8, gap: space.s5 }}>
          {REASONS.map((r) => (
            <View key={r.title} style={{ flexDirection: 'row', gap: space.s4, alignItems: 'flex-start' }}>
              <View style={{
                width: 44, height: 44, borderRadius: radius.full, backgroundColor: colors.canvas1,
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icon name={r.icon} size={20} color={colors.primary500} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="bodyLg" weight="600">{r.title}</AppText>
                <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 2 }}>{r.body}</AppText>
              </View>
            </View>
          ))}
        </View>

        <View style={{ flex: 1 }} />

        {!canAsk ? (
          <AppText variant="bodySm" tone="tertiary" style={{ textAlign: 'center', marginBottom: space.s3 }}>
            {t('alerts_settings_hint')}
          </AppText>
        ) : null}
        <Button
          title={canAsk ? t('alerts_cta') : t('alerts_cta_settings')}
          size="lg"
          loading={busy}
          onPress={() => void enable()}
        />
        <Pressable accessibilityRole="button" onPress={leave} hitSlop={8} style={{ alignSelf: 'center', paddingVertical: space.s4, marginBottom: space.s2 }}>
          <AppText variant="bodySm" weight="500" tone="tertiary">{t('alerts_skip')}</AppText>
        </Pressable>
        <View style={{ alignItems: 'center', paddingBottom: space.s4 }}>
          <BrandLockup markHeight={18} />
        </View>
      </ScrollView>
    </Screen>
  );
}
